/* eslint-disable */
import MinMaxInput from '@/Components/AddProductParameters/minMaxInput';
import { Button } from '@/Components/ui/button';
import { urls } from '@/Constants';
import { Tooltip } from '@mui/material';
import { Trash2, Check, RefreshCcw, Settings2, FilePenLine, X, TriangleAlert } from 'lucide-react';
import moment from 'moment';
import { SelectPicker, Whisper, Popover } from 'rsuite';

// Common render function for long-term production steps
const longTermProductionStepRender = ({ rowData, rowIndex, header, context }) => {
    const { handleDeleteRow, handleChange, handleBlur, viewMode, state } = context;
    switch (header.id) {
        case 'S.No':
            return (
                <div className='flex items-center justify-center text-[12px]'>
                    {rowIndex + 1}
                </div>
            );

        case 'productName':
            return (
                <div className='flex items-center w-full justify-start text-left text-[12px] px-2 whitespace-normal break-words leading-tight py-1'>
                    {rowData.productName || '-'}
                </div>
            );

        case 'clientVariantId':
            return (
                <div className='flex items-center justify-center text-[12px]'>
                    {rowData.clientVariantId || '-'}
                </div>
            );

        case 'batch':
            if (!rowData?.batch) {
                return <Button onClick={() => window.open(`${urls.EXTENSION}${urls.VARIANT_BATCH_SIZE}?productId=${encodeURIComponent(rowData._productId)}&variantId=${encodeURIComponent(rowData._variantId)}&storeId=${encodeURIComponent(state?.kitchen)}&productName=${encodeURIComponent(rowData.productName)}`)} className='px-2 h-6 text-[11px] bg-blue-400 hover:bg-blue-500 text-white' > Add Batch Size</Button>
            }
            return (
                <div className='flex items-center justify-center text-[12px]'>
                    {rowData.batch || '-'} {rowData?.batch && `${rowData?.unit}`}
                </div>
            );

        case 'quantity':
            const batchVal = parseFloat(rowData.batch);
            const qtyVal = parseFloat(rowData.qty);
            const isInvalidMultiple = qtyVal > 0 && batchVal > 0 && (qtyVal * 1000) % (batchVal * 1000) !== 0;
            const isTouched = rowData[`_touched_${header.id}`];
            const isBatchMissing = !rowData.batch || batchVal === 0;

            return (
                <div className='flex flex-col items-center justify-center gap-1'>
                    <input
                        type='number'
                        className={`text-center w-[100px] border focus:border-blue-500 outline-none transition-colors duration-200 rounded px-2 py-1 text-[12px] ${isInvalidMultiple && isTouched ? 'border-red-500' : 'border-gray-300'
                            } ${isBatchMissing ? 'bg-gray-100 cursor-not-allowed border-gray-200' : ''}`}
                        value={rowData.qty ?? ''}
                        disabled={viewMode || isBatchMissing}
                        onChange={(e) => handleChange(e.target.value, null, rowData)}
                        onBlur={() => handleBlur(rowIndex, header.id)}
                        onKeyDown={(e) => ['-', '+', "'", 'e', 'E'].includes(e.key) && e.preventDefault()}
                        placeholder={isBatchMissing ? 'N/A' : 'Enter qty'}
                    />
                    {isBatchMissing ? (
                        <span className='text-red-500 text-[10px] whitespace-nowrap font-medium'>
                            Batch size not defined
                        </span>
                    ) : isInvalidMultiple && isTouched && (
                        <span className='text-red-500 text-[10px]'>
                            Multiple of {rowData?.batch} {rowData?.batch && rowData?.unit}
                        </span>
                    )}
                </div>
            );

        case 'remove':
            return (
                <div className='flex items-center justify-center'>
                    <Trash2
                        className='w-4 h-4 cursor-pointer text-red-500 hover:text-red-700 transition-colors'
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRow(rowIndex);
                        }}
                        title='Remove row'
                    />
                </div>
            );

        default:
            // Return null to fallback to default renderer
            return (
                <div className='flex items-center justify-center text-[12px] px-2'>
                    {rowData[header.id] ?? '-'}
                </div>
            );
    }
};

const longTermProductionSecondStepRender = ({ rowData, rowIndex, header, context }) => {
    const { handleCheckBoxChange, handleFrequencyChange, handleRepeatDaysChange, viewMode, handleCustomDates, workerAllocations } = context;
    if (String(header.id).startsWith('date_')) {
        const isScheduled = rowData[header.id];
        const dateStr = header.id.replace('date_', '');
        const isPastDate = moment(dateStr).isBefore(moment(), 'day');
        const allocation = workerAllocations?.[dateStr];
        const isHoliday = allocation?.isHoliday !== undefined && allocation.isHoliday;
        const isDisabled = isPastDate || isHoliday;

        return (
            <div className='flex items-center justify-center w-full h-full'>
                <div
                    className={`w-5 h-5 flex items-center justify-center rounded border transition-colors duration-200 ${isDisabled
                        ? 'bg-gray-100 border-gray-300 cursor-not-allowed'
                        : isScheduled
                            ? 'bg-green-600 border-green-600 shadow-sm cursor-pointer'
                            : 'bg-white border-gray-800 hover:border-blue-400 cursor-pointer'
                        }`}
                    onClick={() => !viewMode && !isDisabled && handleCheckBoxChange(!isScheduled, rowIndex, header)}
                >
                    {isDisabled ? (
                        <X className='w-3.5 h-3.5 text-gray-400 stroke-[3]' />
                    ) : (
                        isScheduled && <Check className='w-3.5 h-3.5 text-white stroke-[3]' />
                    )}
                </div>
            </div>
        );
    }
    switch (header.id) {
        case 'frequency':
            const frequencyOptions = [
                { label: 'Daily', value: 'daily' },
                { label: 'Repeat After', value: 'repeat' },
                { label: 'Custom', value: 'custom' },
            ];
            const hasSelectedDate = Object.keys(rowData).some(key => String(key).startsWith('date_') && rowData[key] === true);
            return (
                <div className='flex flex-col gap-2 items-center justify-center px-1 w-full'>
                    {!hasSelectedDate && rowData.frequency === 'repeat' && (
                        <div
                            className='text-blue-500 text-[10px] font-semibold mt-1 text-center'
                            style={{
                                animation: 'pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                            }}
                        >
                            Please select a starting <br /> date to repeat
                        </div>
                    )}
                    <SelectPicker
                        data={frequencyOptions}
                        value={rowData?.frequency}
                        size='sm'
                        cleanable={false}
                        searchable={false}
                        menuStyle={{
                            zIndex: 4000,
                        }}
                        className='w-full min-w-[100px] text-[12px] [&_.rs-picker-toggle]:!h-8'
                        disabled={viewMode}
                        onChange={(value) => handleFrequencyChange(value, rowIndex, header)}
                    />
                    {rowData.frequency === 'repeat' && (
                        <MinMaxInput
                            id={rowIndex}
                            value={rowData.repeatDaysNumber || ''}
                            handleMinMaxChange={handleRepeatDaysChange}
                            notNegative={true}
                            disabled={viewMode}
                            name='repeatDaysNumber'
                            size='sm'
                        />
                    )}
                    {rowData?.frequency === 'custom' && (
                        <div className='flex items-center gap-1 mt-2'>
                            <button
                                onClick={() => handleCustomDates(rowIndex, true)}
                                className='px-2 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-600 text-[10px] font-bold hover:bg-blue-100 transition-colors shadow-sm'
                            >
                                Select All
                            </button>
                            <button
                                onClick={() => handleCustomDates(rowIndex, false)}
                                className='px-2 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-600 text-[10px] font-bold hover:bg-blue-100 transition-colors shadow-sm'
                            >
                                DeSelect All
                            </button>
                        </div>
                    )}
                </div>
            );

        case 'S.No':
            return (
                <div className='flex items-center justify-center text-[12px]'>
                    {rowIndex + 1}
                </div>
            );

        case 'productName':
            return (
                <div className='flex items-center w-full justify-start text-left text-[12px] px-2 whitespace-normal break-words leading-tight py-1'>
                    {rowData.productName || '-'}{`${rowData?.clientVariantId}`}
                </div>
            );

        default:
            // Return null to fallback to default renderer
            return (
                <div className='flex items-center justify-center text-[12px] px-2'>
                    {rowData[header.id] ?? '-'}
                </div>
            );
    }
};

const longTermProductionThirdStepRender = ({ rowData, rowIndex, header, context }) => {
    const { handleDateQtyChange, handleBlur, viewMode, workerAllocations } = context;
    if (String(header.id).startsWith('date_')) {
        const { editMode, state } = context;
        const dateStr = header.id.replace('date_', '');
        const isPastDate = moment(dateStr).isBefore(moment(), 'day');
        const hasGeneratedPlan = state?.generatedPlans?.some(
            plan => (moment(plan.scheduledAt).isSame(moment(dateStr), 'day') && plan?.status !== 'CANCELLED')
        );
        const isHoliday = workerAllocations[dateStr]?.isHoliday;
        if (editMode && (isPastDate || hasGeneratedPlan)) {
            const plannedQty = rowData[header.id] === false ? 0 : parseFloat(rowData[header.id]) || 0;
            const fulfilledQty = parseFloat(rowData[`_fulfilled_${header.id}`]) || 0;

            let bgColor = 'bg-slate-200';
            let textColor = 'text-slate-700';

            if (fulfilledQty < plannedQty) {
                bgColor = 'bg-red-200';
                textColor = 'text-red-700';
            } else if (fulfilledQty > plannedQty) {
                bgColor = 'bg-green-200';
                textColor = 'text-green-700';
            }

            return (
                <div className={`flex flex-col items-center justify-center w-[80px] h-[40px] rounded-md ${bgColor} ${textColor} text-[11px] font-medium transition-colors`}>
                    <div className="font-bold">{fulfilledQty}/</div>
                    <div>{plannedQty}</div>
                </div>
            );
        }

        const batchSize = parseFloat(rowData.batch);
        const dayQty = parseFloat(rowData[header.id]);
        const isInvalidDayQty = dayQty > 0 && batchSize > 0 && (dayQty * 1000) % (batchSize * 1000) !== 0;
        const isDayTouched = rowData[`_touched_${header.id}`];
        const isBatchMissing = !rowData.batch || batchSize === 0;
        const shouldShowDash = editMode ? (isPastDate || isHoliday) : rowData[header.id] === false;

        return (
            <div className='flex flex-col items-center justify-center gap-1 w-[80px]'>
                <div className='flex items-center justify-center w-full h-[28px] primary-input-container'>
                    {shouldShowDash ? (
                        <div className='flex items-center justify-center text-[12px] text-gray-400 bg-gray-100 w-full h-full rounded'>
                            -
                        </div>
                    ) : (
                        <input
                            type='number'
                            className={`text-center w-full h-full border focus:border-blue-500 outline-none transition-colors duration-200 rounded px-2 text-[12px] ${isInvalidDayQty && isDayTouched ? 'border-red-500' : 'border-gray-300'
                                } ${isBatchMissing ? 'bg-gray-100 cursor-not-allowed border-gray-200' : ''}`}
                            value={rowData[header.id] || 0}
                            disabled={viewMode || isBatchMissing}
                            onChange={(e) => handleDateQtyChange(e.target.value, rowIndex, header)}
                            onBlur={() => handleBlur(rowIndex, header.id)}
                            onKeyDown={(e) => ['-', '+', "'", 'e', 'E'].includes(e.key) && e.preventDefault()}
                            placeholder={isBatchMissing ? 'N/A' : 'Enter qty'}
                        />
                    )}
                </div>
                {rowData[header.id] !== false && (isBatchMissing ? (
                    <span className='text-red-500 text-[9px] leading-[1.1] block text-center mt-1 font-medium'>
                        Batch size not defined
                    </span>
                ) : isInvalidDayQty && isDayTouched && (
                    <span className='text-red-500 text-[9px] leading-[1.1] block text-center mt-1'>
                        Multiple of {rowData.batch} {rowData?.unit} required
                    </span>
                ))}
            </div>
        );
    }
    switch (header.id) {
        case 'S.No':
            return (
                <div className='flex items-center justify-center text-[12px]'>
                    {rowIndex + 1}
                </div>
            );

        case 'productName':
            return (
                <div className='flex items-center w-full justify-start text-left text-[12px] px-2 whitespace-normal break-words leading-tight py-1'>
                    {rowData.productName || '-'} {rowData?.clientVariantId}
                </div>
            );

        case 'clientVariantId':
            return (
                <div className='flex items-center justify-center text-[12px]'>
                    {rowData.clientVariantId || '-'}
                </div>
            );
        case 'batch':
            return (
                <div className='flex items-center justify-center text-[12px]'>
                    {rowData.batch || '-'} {rowData?.batch && rowData?.unit}
                </div>
            );
        case 'planned':
            return (
                <div className='flex items-center font-bold justify-center text-[12px] w-full'>
                    {rowData.planned || 0} {rowData?.unit}
                </div>
            );
        case 'variance':
            const { handleVarianceAdjust, handleVarianceWriteOff, editMode, state } = context;
            const batchSizeForVal = parseFloat(rowData.batch);
            const today = moment().startOf('day');
            const hasInvalidQty = Object.keys(rowData).some(key => {
                if (String(key).startsWith('date_')) {
                    const dateStr = String(key).replace('date_', '');
                    const isPastDate = moment(dateStr).isBefore(today);
                    const hasGeneratedPlan = state?.generatedPlans?.some(
                        plan => moment(plan.scheduledAt).isSame(moment(dateStr), 'day')
                    );
                    if (isPastDate || hasGeneratedPlan) return false;

                    const val = parseFloat(rowData[key]);
                    return val > 0 && batchSizeForVal > 0 && (val * 1000) % (batchSizeForVal * 1000) !== 0;
                }
                return false;
            });
            const speaker = (
                <Popover
                    className='!p-0 border-none shadow-lg rounded-md overflow-hidden'
                    style={{ zIndex: 9999 }}
                >
                    <div className='flex flex-col p-1 bg-white min-w-[220px]'>
                        {hasInvalidQty && (
                            <div className='px-2 py-2 text-red-500 text-[11px] font-bold border-b border-red-100 bg-red-50 text-center'>
                                The entering qty is not the multiple of batch size
                            </div>
                        )}
                        <div
                            className='flex items-center gap-4 px-2 py-2 hover:bg-gray-50 cursor-pointer transition-colors group'
                            onClick={() => {
                                if (hasInvalidQty) return;
                                handleVarianceAdjust(rowIndex);
                            }}
                        >
                            <Settings2 size={20} className='text-black' />
                            <span className='text-[14px] text-black'>Adjust in remaining days</span>
                        </div>
                        <div
                            className='flex items-center gap-4 px-2 py-2 bg-gray-100 hover:bg-gray-200 cursor-pointer transition-colors group'
                        // onClick={() => {
                        //     handleVarianceWriteOff(rowIndex);
                        // }}
                        >
                            <FilePenLine size={20} className='text-black' />
                            <span className='text-[14px] text-black'>Write Off</span>
                        </div>
                    </div>
                </Popover>
            );
            return (
                <div className='flex flex-col items-center justify-center gap-1 text-[12px] w-full'>
                    {hasInvalidQty && (
                        <Tooltip title='Production quantities must be multiples of the batch size'>
                            <div className='flex items-center gap-1 px-1 py-0.5 bg-red-50 border border-red-200 rounded text-red-600 cursor-help shrink-0'>
                                <TriangleAlert size={10} className='shrink-0' />
                                <span className='text-[8px] font-bold whitespace-nowrap leading-none'>INVALID QUANTITIES</span>
                            </div>
                        </Tooltip>
                    )}
                    <div className='flex items-center gap-2 justify-center'>
                        <span className={`${(rowData.variance || 0) > 0 ? 'text-green-600' : (rowData.variance || 0) < 0 ? 'text-red-600' : ''}`}>
                            {rowData.variance > 0 ? `+${rowData.variance}` : rowData.variance || 0}
                        </span>
                        {editMode && parseFloat(rowData.variance) !== 0 && (
                            <Whisper
                                placement='bottomStart'
                                trigger='click'
                                speaker={speaker}
                                enterable
                            >
                                <div className='cursor-pointer p-1 hover:bg-gray-100 rounded-full transition-colors'>
                                    <RefreshCcw className='text-black' size={16} />
                                </div>
                            </Whisper>
                        )}
                    </div>
                </div>
            );
        case 'finalProduction':
            return (
                <div className='flex items-center font-bold justify-center text-[12px] w-full'>
                    {rowData.finalProduction || 0} {rowData?.unit}
                </div>
            );
        default:
            // Return null to fallback to default renderer
            return (
                <div className='flex items-center justify-center text-[12px] px-2'>
                    {rowData[header.id] ?? '-'}
                </div>
            );
    }
};

export const tableRenderRowData = {
    longTermProduction: {
        1: {
            render: longTermProductionStepRender,
        },
        2: {
            render: longTermProductionSecondStepRender,
        },
        3: {
            render: longTermProductionThirdStepRender,
        },
    },
};

export const getTableRenderer = (tableRenderKey, currentStep = 1) => {
    const config = tableRenderRowData[tableRenderKey];
    if (!config) return null;
    if (config[currentStep]?.render) {
        return config[currentStep].render;
    }

    return config.render || null;
};
